import { classNames, NonTimezoneSensitiveDate } from '@commenda/utils';
import { useState } from 'react';
import { useToggle } from '../../../hooks/useToggle';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { DatePicker } from '../../DatePicker';
import { DateRangePicker } from '../../DateRangePicker';
import { DEFAULT_PREDEFINED_RANGES } from '../../DateRangePicker/constants';
import { calculateDateRange } from '../../DateRangePicker/utils';
import { Icons } from '../../Icons';
import { Input } from '../../Input';
import LoadingIndicator from '../../LoadingIndicator/LoadingIndicator';
import { Popover, PopoverContent, PopoverTrigger } from '../../Popover/Popover';
import { RadioGroup, RadioGroupItem } from '../../RadioGroup';
import { Separator } from '../../Separator';
import { Tooltip } from '../../Tooltip';
import { useRemoteFilterOptions } from '../hooks/useRemoteFilterOptions';
import { FilterConfig, TableFilter } from '../types';
import { TableFilterOptionsHelper } from '../utils/filter-options';
import { TableGlobalActionTrigger } from './TableGlobalActionTrigger';

const GlobalFilterTool = ({
  filterConfig,
}: {
  filterConfig?: FilterConfig;
}) => {
  const { value: open, setValue: setOpen } = useToggle();
  const [selectedFilterId, setSelectedFilterId] = useState(
    filterConfig?.filters[0]?.columnId
  );
  const selectedFilter =
    filterConfig?.filters.find(
      (filter) => filter.columnId === selectedFilterId
    ) ?? filterConfig?.filters[0];
  const [searchValue, setSearchValue] = useState('');
  // Store partial date selections locally (not in filter) to maintain DateRangePicker state
  const [partialDateSelection, setPartialDateSelection] = useState<{
    fromDate?: string;
    toDate?: string;
  } | null>(null);

  const { filters, activeFilters = {}, onFilterChange } = filterConfig || {};

  const remote = useRemoteFilterOptions(
    selectedFilter?.loadOptions,
    searchValue,
    open
  );

  if (!filterConfig || !filterConfig.filters.length) {
    return null;
  }

  const getActiveFilterCount = () => {
    return Object.keys(activeFilters).filter(
      (key) =>
        (Array.isArray(activeFilters[key]) && activeFilters[key].length > 0) ||
        (typeof activeFilters[key] === 'string' && activeFilters[key] !== '')
    ).length;
  };

  const clearAllFilters = () => {
    onFilterChange?.({});
  };

  const handleFilterOptionClick = (
    filter: TableFilter,
    value: string | number
  ) => {
    const currentValue = activeFilters[filter.columnId];

    let newValue: string | string[];

    if (filter.type === 'MULTI_SELECT') {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      const normalizedCurrentArray = currentArray.map((v) => String(v));
      const normalizedValue = String(value);
      const newArray = normalizedCurrentArray.includes(normalizedValue)
        ? currentArray.filter((v) => String(v) !== normalizedValue)
        : [...currentArray, normalizedValue];
      if (filter.maxSelections && newArray.length > filter.maxSelections)
        return;
      newValue = newArray;
    } else {
      newValue = String(value);
    }

    onFilterChange?.({
      ...activeFilters,
      [filter.columnId]: newValue,
    });
  };

  // Get predefined ranges for DateRangePicker
  const getPredefinedRanges = (): { value: string; label: string }[] => {
    if (selectedFilter?.options && selectedFilter.options.length > 0) {
      return selectedFilter.options.map((opt) => ({
        value: opt.value.toString(),
        label: opt.label,
      }));
    }
    return DEFAULT_PREDEFINED_RANGES;
  };

  // Convert filter value format to DateRangePicker format
  const parseFilterValueToDates = (
    filterValue: string | string[] | undefined
  ): { fromDate?: string; toDate?: string } => {
    // If there's a partial selection in progress, use that instead of the stored filter
    // This allows the DateRangePicker to maintain state during range selection
    if (partialDateSelection) {
      return partialDateSelection;
    }

    if (!filterValue || typeof filterValue !== 'string') {
      return { fromDate: undefined, toDate: undefined };
    }

    if (filterValue.startsWith('custom:')) {
      const [, rangeStr] = filterValue.split(':');
      const [fromStr, toStr] = rangeStr.split(',');
      if (fromStr && toStr) {
        // Convert YYYY-MM-DD to ISO8601 format for DateRangePicker
        try {
          const fromDate = NonTimezoneSensitiveDate.fromISO8601String(
            fromStr.trim()
          ).toISO8601String();
          const toDate = NonTimezoneSensitiveDate.fromISO8601String(
            toStr.trim()
          ).toISO8601String();
          return {
            fromDate,
            toDate,
          };
        } catch {
          // If parsing fails, return as-is (shouldn't happen with valid dates)
          return {
            fromDate: fromStr.trim(),
            toDate: toStr.trim(),
          };
        }
      }
    } else if (filterValue.startsWith('preset:')) {
      const code = filterValue.replace('preset:', '');
      const range = calculateDateRange(code);
      return {
        fromDate: range.from_date,
        toDate: range.to_date,
      };
    }

    return { fromDate: undefined, toDate: undefined };
  };

  // Normalize date string to YYYY-MM-DD format (extract just the date part from ISO8601)
  const normalizeDate = (dateStr: string | undefined): string | undefined => {
    if (!dateStr) return undefined;
    // Extract just the date part (YYYY-MM-DD) from ISO8601 string
    return dateStr.split('T')[0];
  };

  // Convert DateRangePicker format to filter value format
  const convertDatesToFilterValue = (
    fromDate: string | undefined,
    toDate: string | undefined
  ): string | undefined => {
    if (!fromDate || !toDate) return undefined;

    // Normalize dates to YYYY-MM-DD format for comparison
    const normalizedFrom = normalizeDate(fromDate);
    const normalizedTo = normalizeDate(toDate);

    if (!normalizedFrom || !normalizedTo) return undefined;

    // Check if this matches a predefined range
    const predefinedRanges = getPredefinedRanges();

    for (let i = 0; i < predefinedRanges.length; i += 1) {
      const range = predefinedRanges[i];

      if (range.value === 'custom') continue;

      const calculatedRange = calculateDateRange(range.value);
      const normalizedCalculatedFrom = normalizeDate(calculatedRange.from_date);
      const normalizedCalculatedTo = normalizeDate(calculatedRange.to_date);

      if (
        normalizedCalculatedFrom === normalizedFrom &&
        normalizedCalculatedTo === normalizedTo
      ) {
        return `preset:${range.value}`;
      }
    }

    // If no predefined range matches, store as custom (using normalized dates)
    return `custom:${normalizedFrom},${normalizedTo}`;
  };

  // Handle date range change from DateRangePicker
  const handleDateRangeChange = (
    fromDate: string | undefined,
    toDate: string | undefined
  ) => {
    if (!selectedFilter || selectedFilter.type !== 'DATE_RANGE') return;

    // If both dates are undefined, clear the filter and partial selection
    if (!fromDate && !toDate) {
      setPartialDateSelection(null);
      const newFilters = { ...activeFilters };
      delete newFilters[selectedFilter.columnId];
      onFilterChange?.(newFilters);
      return;
    }

    if (fromDate || toDate) setPartialDateSelection({ fromDate, toDate });

    if (fromDate && toDate) {
      const normalizedFrom = normalizeDate(fromDate);
      const normalizedTo = normalizeDate(toDate);

      if (normalizedFrom && normalizedTo && normalizedFrom > normalizedTo)
        return;

      const filterValue = convertDatesToFilterValue(fromDate, toDate);
      if (filterValue) {
        setPartialDateSelection(null);
        onFilterChange?.({
          ...activeFilters,
          [selectedFilter.columnId]: filterValue,
        });
      }
    }
  };

  const handleDateChange = (date: string | undefined) => {
    if (!selectedFilter || selectedFilter.type !== 'DATE') return;

    const normalized = normalizeDate(date);
    if (!normalized) {
      const newFilters = { ...activeFilters };
      delete newFilters[selectedFilter.columnId];
      onFilterChange?.(newFilters);
      return;
    }

    onFilterChange?.({
      ...activeFilters,
      [selectedFilter.columnId]: normalized,
    });
  };

  const toggleSelectAll = (filter: TableFilter) => {
    const currentValue = activeFilters[filter.columnId];
    const currentArray = Array.isArray(currentValue) ? currentValue : [];

    if (currentArray.length === (filter.options?.length || 0)) {
      onFilterChange?.({
        ...activeFilters,
        [filter.columnId]: [],
      });
    } else {
      // Select all
      const allValues = filter.options?.map((opt) => String(opt.value)) || [];
      onFilterChange?.({
        ...activeFilters,
        [filter.columnId]: allValues,
      });
    }
  };

  const renderFilterContent = () => {
    if (!selectedFilter) return null;

    const filteredOptions = selectedFilter.loadOptions
      ? TableFilterOptionsHelper.mergeOptions(
          selectedFilter.options ?? [],
          remote.options
        )
      : selectedFilter.options?.filter((option) =>
          (option.label ?? '').toLowerCase().includes(searchValue.toLowerCase())
        ) || [];

    // Check if the current filter has an active value
    const currentFilterValue = activeFilters[selectedFilter.columnId];
    const hasActiveFilterForCurrentCategory =
      currentFilterValue &&
      (Array.isArray(currentFilterValue)
        ? currentFilterValue.length > 0
        : currentFilterValue !== '');

    // Separate selected and unselected options to show selected at top
    const getSelectedAndUnselectedOptions = () => {
      const selected: typeof filteredOptions = [];
      const unselected: typeof filteredOptions = [];

      filteredOptions.forEach((option) => {
        if (selectedFilter.type === 'MULTI_SELECT') {
          const isSelected = Array.isArray(currentFilterValue)
            ? currentFilterValue
                .map((v) => String(v))
                .includes(String(option.value))
            : String(currentFilterValue) === String(option.value);

          if (isSelected) {
            selected.push(option);
          } else {
            unselected.push(option);
          }
        } else if (selectedFilter.type === 'SINGLE_SELECT') {
          const isSelected =
            String(currentFilterValue) === String(option.value);

          if (isSelected) {
            selected.push(option);
          } else {
            unselected.push(option);
          }
        } else {
          unselected.push(option);
        }
      });

      return { selected, unselected };
    };

    const { selected: selectedOptions, unselected: unselectedOptions } =
      getSelectedAndUnselectedOptions();

    return (
      <div className="flex flex-col min-h-full">
        <div className="flex flex-col gap-md flex-1 min-h-0 px-xs">
          {selectedFilter.type === 'SINGLE_SELECT' && (
            <div className="flex flex-col gap-lg pb-2xl">
              <div className="flex items-center gap-2.5">
                <span className="txt-extra-small-medium text-text-secondary">
                  {selectedFilter.label || 'Select an option'}
                </span>
                <div className="h-px flex-1 bg-border-primary" />
                {hasActiveFilterForCurrentCategory ? (
                  <Button
                    type="button"
                    variant="brand-link"
                    size="xs"
                    onClick={() => {
                      const newFilters = { ...activeFilters };
                      if (selectedFilter.type === 'MULTI_SELECT') {
                        newFilters[selectedFilter.columnId] = [];
                      } else {
                        delete newFilters[selectedFilter.columnId];
                      }
                      onFilterChange?.(newFilters);
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>

              {filteredOptions.length === 0 ? (
                <p className="txt-extra-small-regular text-text-tertiary">
                  No options available for selected filter
                </p>
              ) : (
                <RadioGroup className="flex flex-col gap-lg">
                  {/* Selected options first */}
                  {selectedOptions.map((option) => {
                    const isSelected =
                      String(activeFilters[selectedFilter.columnId]) ===
                      String(option.value);

                    return (
                      <button
                        key={`selected-${option.value}`}
                        type="button"
                        onClick={() =>
                          handleFilterOptionClick(selectedFilter, option.value)
                        }
                        className="flex items-center gap-md group w-full min-w-0"
                      >
                        <RadioGroupItem
                          value={String(option.value)}
                          checked={isSelected}
                          className="size-4 shrink-0 cursor-pointer accent-bg-brand"
                        />
                        <Tooltip content={String(option.label)} side="top">
                          <span className="txt-small-regular text-text-tertiary text-left truncate min-w-0 flex-1">
                            {option.label}
                          </span>
                        </Tooltip>
                      </button>
                    );
                  })}

                  {/* Separator if we have both selected and unselected options */}
                  {selectedOptions.length > 0 &&
                    unselectedOptions.length > 0 && (
                      <div className="h-px bg-border-primary my-sm" />
                    )}

                  {/* Unselected options */}
                  {unselectedOptions.map((option) => {
                    return (
                      <button
                        key={`unselected-${option.value}`}
                        disabled={Boolean(
                          selectedFilter.maxSelections &&
                            Array.isArray(currentFilterValue) &&
                            currentFilterValue.length >=
                              selectedFilter.maxSelections
                        )}
                        type="button"
                        onClick={() =>
                          handleFilterOptionClick(selectedFilter, option.value)
                        }
                        className="flex items-center gap-md group w-full min-w-0"
                      >
                        <RadioGroupItem
                          value={String(option.value)}
                          className="size-4 shrink-0 cursor-pointer accent-bg-brand"
                        />
                        <Tooltip content={String(option.label)} side="top">
                          <span className="txt-small-regular text-text-tertiary text-left truncate min-w-0 flex-1">
                            {option.label}
                          </span>
                        </Tooltip>
                      </button>
                    );
                  })}
                </RadioGroup>
              )}
            </div>
          )}

          {selectedFilter.type === 'MULTI_SELECT' && (
            <div className="flex flex-col gap-lg pb-2xl">
              <div className="flex items-center gap-2.5">
                <span className="txt-extra-small-medium text-text-secondary">
                  {selectedFilter.label || 'Select options'}
                </span>
                <div className="h-px flex-1 bg-border-primary" />
                {(filteredOptions.length > 0 ||
                  hasActiveFilterForCurrentCategory) && (
                  <div className="flex items-center gap-2.5">
                    {!selectedFilter.loadOptions &&
                      selectedFilter.showSelectAll !== false &&
                      filteredOptions.length > 0 && (
                        <Button
                          type="button"
                          variant="brand-link"
                          size="xs"
                          onClick={() => toggleSelectAll(selectedFilter)}
                        >
                          Select all
                        </Button>
                      )}
                    {hasActiveFilterForCurrentCategory && (
                      <>
                        <div className="h-4 w-px bg-border-primary" />
                        <Button
                          type="button"
                          variant="brand-link"
                          size="xs"
                          onClick={() => {
                            const newFilters = { ...activeFilters };
                            newFilters[selectedFilter.columnId] = [];
                            onFilterChange?.(newFilters);
                          }}
                        >
                          Clear
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
              {filteredOptions.length === 0 ? (
                <p className="txt-extra-small-regular text-text-tertiary">
                  No options available for selected filter
                </p>
              ) : (
                <div className="flex flex-col gap-lg">
                  {/* Selected options first */}
                  {selectedOptions.map((option) => {
                    const currentValue = activeFilters[selectedFilter.columnId];
                    const isSelected = Array.isArray(currentValue)
                      ? currentValue
                          .map((v) => String(v))
                          .includes(String(option.value))
                      : String(currentValue) === String(option.value);

                    return (
                      <button
                        key={`selected-${option.value}`}
                        type="button"
                        onClick={() =>
                          handleFilterOptionClick(selectedFilter, option.value)
                        }
                        className="flex items-center gap-md group w-full min-w-0"
                      >
                        <Checkbox
                          checked={isSelected}
                          className="size-4 shrink-0 cursor-pointer"
                        />
                        <Tooltip content={String(option.label)} side="top">
                          <span className="txt-small-regular text-text-tertiary text-left truncate min-w-0 flex-1">
                            {option.label}
                          </span>
                        </Tooltip>
                      </button>
                    );
                  })}

                  {/* Separator if we have both selected and unselected options */}
                  {selectedOptions.length > 0 &&
                    unselectedOptions.length > 0 && (
                      <div className="h-px bg-border-primary my-sm" />
                    )}

                  {/* Unselected options */}
                  {unselectedOptions.map((option) => {
                    return (
                      <button
                        key={`unselected-${option.value}`}
                        disabled={Boolean(
                          selectedFilter.maxSelections &&
                            Array.isArray(currentFilterValue) &&
                            currentFilterValue.length >=
                              selectedFilter.maxSelections
                        )}
                        type="button"
                        onClick={() =>
                          handleFilterOptionClick(selectedFilter, option.value)
                        }
                        className="flex items-center gap-md group w-full min-w-0"
                      >
                        <Checkbox className="size-4 shrink-0 cursor-pointer" />
                        <Tooltip content={String(option.label)} side="top">
                          <span className="txt-small-regular text-text-tertiary text-left truncate min-w-0 flex-1">
                            {option.label}
                          </span>
                        </Tooltip>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selectedFilter.type === 'DATE_RANGE' && (
            <div className="flex flex-col gap-lg">
              <div className="flex items-center gap-2.5">
                <span className="txt-extra-small-medium text-text-secondary">
                  {selectedFilter.label || 'Select date range'}
                </span>
                <div className="h-px flex-1 bg-border-primary" />
                {hasActiveFilterForCurrentCategory ? (
                  <Button
                    type="button"
                    variant="brand-link"
                    size="xs"
                    onClick={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters[selectedFilter.columnId];
                      onFilterChange?.(newFilters);
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
              <DateRangePicker
                label={selectedFilter.label || 'Date range'}
                fromDate={
                  parseFilterValueToDates(
                    activeFilters[selectedFilter.columnId]
                  ).fromDate
                }
                toDate={
                  parseFilterValueToDates(
                    activeFilters[selectedFilter.columnId]
                  ).toDate
                }
                onDateChange={handleDateRangeChange}
                predefinedRanges={getPredefinedRanges()}
                disableFutureDays={selectedFilter.disableFutureDays}
                asChild
              />
            </div>
          )}

          {selectedFilter.type === 'DATE' && (
            <div className="flex flex-col gap-lg">
              <div className="flex items-center gap-2.5">
                <span className="txt-extra-small-medium text-text-secondary">
                  {selectedFilter.label || 'Select date'}
                </span>
                <div className="h-px flex-1 bg-border-primary" />
                {hasActiveFilterForCurrentCategory ? (
                  <Button
                    type="button"
                    variant="brand-link"
                    size="xs"
                    onClick={() => {
                      const newFilters = { ...activeFilters };
                      delete newFilters[selectedFilter.columnId];
                      onFilterChange?.(newFilters);
                    }}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
              <DatePicker
                date={
                  typeof currentFilterValue === 'string'
                    ? currentFilterValue
                    : undefined
                }
                setDate={handleDateChange}
                placeholder={selectedFilter.placeholder || 'Select date'}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const activeCount = getActiveFilterCount();

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <TableGlobalActionTrigger
          count={activeCount}
          onClear={clearAllFilters}
          icon={<Icons.FilterLines className="size-4" />}
          label="Filters"
        />
      </PopoverTrigger>
      <PopoverContent
        className="min-w-[594px] p-0 overflow-clip"
        align="start"
        sideOffset={8}
      >
        <div className="flex h-[400px]">
          {/* Left sidebar with filter categories */}
          <div className="w-[220px] bg-bg-secondary flex flex-col gap-lg px-xl py-lg">
            <div className="flex items-center justify-between gap-2.5">
              <span className="txt-extra-small-medium text-text-secondary">
                Filters
              </span>
              <Separator
                className={classNames(
                  'bg-border-primary',
                  activeCount ? 'w-[86px]' : 'w-[143px]'
                )}
              />
              {activeCount ? (
                <Button
                  type="button"
                  variant="brand-link"
                  size="xs"
                  onClick={clearAllFilters}
                >
                  Clear all
                </Button>
              ) : null}
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col gap-xxs">
              {(filters || []).map((filter) => {
                const isFilterActive =
                  activeFilters[filter.columnId] &&
                  (Array.isArray(activeFilters[filter.columnId])
                    ? activeFilters[filter.columnId].length > 0
                    : activeFilters[filter.columnId] !== '');

                const isSelected = selectedFilter?.columnId === filter.columnId;

                return (
                  <button
                    key={filter.columnId}
                    type="button"
                    onClick={() => {
                      setSelectedFilterId(filter.columnId);
                      setSearchValue('');
                      // Clear partial date selection when switching filters
                      if (filter.type !== 'DATE_RANGE') {
                        setPartialDateSelection(null);
                      }
                    }}
                    className={classNames(
                      'w-full flex items-center gap-lg px-md py-sm transition-all duration-200 text-text-tertiary hover:text-text-tertiary-hover hover:bg-bg-active-dark rounded-md',
                      isSelected ? 'bg-bg-active-dark' : 'bg-bg-secondary'
                    )}
                  >
                    {isFilterActive ? (
                      <div className="size-4 flex justify-center items-center bg-fg-brand shrink-0 rounded-full">
                        <Icons.Check className="size-3 text-fg-white" />
                      </div>
                    ) : (
                      filter.icon
                    )}

                    <span className="txt-small-medium flex-1 text-left">
                      {filter.label || filter.columnId}
                    </span>
                    <Icons.ChevronRight className="size-3 text-text-primary shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right content area with filter options */}
          <div className="flex-1 flex flex-col gap-2xl overflow-y-auto px-2xl pt-2xl">
            {selectedFilter && selectedFilter.showSearch && (
              <Input
                placeholder={selectedFilter.placeholder || 'Search'}
                leftEl={() => (
                  <Icons.SearchSm className="text-text-quaternary size-4" />
                )}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="w-full h-9"
              />
            )}

            <div className="flex-1 overflow-y-auto pb-2xl">
              {selectedFilter ? (
                <>
                  {selectedFilter.loadOptions && (
                    <div
                      className="mb-lg txt-extra-small-regular text-text-tertiary"
                      aria-live="polite"
                    >
                      {remote.loading && <LoadingIndicator size={16} />}
                      {remote.error && (
                        <div role="alert">
                          Could not load options.{' '}
                          <Button
                            type="button"
                            variant="brand-link"
                            size="xs"
                            onClick={remote.retry}
                          >
                            Retry
                          </Button>
                        </div>
                      )}
                      {selectedFilter.maxSelections && (
                        <p>
                          Select up to {selectedFilter.maxSelections} options.
                          Selections are kept across searches.
                        </p>
                      )}
                    </div>
                  )}
                  {renderFilterContent()}
                  {selectedFilter.loadOptions && remote.nextOffset !== null && (
                    <Button
                      type="button"
                      variant="brand-link"
                      size="sm"
                      disabled={remote.loading}
                      onClick={remote.loadMore}
                    >
                      Load more options
                    </Button>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="txt-small-regular text-text-tertiary">
                    Select a filter to view options
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { GlobalFilterTool };
